// Shared marketplace link targets. The legal pages are real in-app routes
// (see App.tsx: /terms-of-service, /privacy-policy), so link to them directly.
export const TERMS_URL = '/terms-of-service';
export const PRIVACY_URL = '/privacy-policy';

import type { StudySelection } from './marketplaceApi';

// In-app marketplace home. This route renders NO page of its own — MarketplaceHome
// is a pure dispatcher that forwards to the run flow or the post-study screen (the
// in-app marketing landing was removed). Link here only when you mean "wherever this
// user belongs right now"; link to MARKETPLACE_RUN_PATH when you mean the wizard.
export const MARKETPLACE_HOME_PATH = '/marketplace';

// Wizard route entry points. The free flow picks its audience in UseCasePicker
// (/marketplace/run), the paid flow in BuildAudienceScreen (/marketplace/run/build).
// Kept here so the wizard routes stay in one place instead of drifting across
// the screens (and App.tsx) as inline literals.
export const MARKETPLACE_RUN_PATH = '/marketplace/run';
export const MARKETPLACE_BUILD_PATH = '/marketplace/run/build';
export const MARKETPLACE_FILL_PATH = '/marketplace/run/fill';
export const MARKETPLACE_REVIEW_PATH = '/marketplace/run/review';
// Where a user who has already spent their free study is sent from /marketplace.
export const MARKETPLACE_DONE_PATH = '/marketplace/done';

/** State carried when navigating BACK to the audience step so the builder can
 *  restore the study context instead of resetting. UseCasePicker restores from
 *  subCategory/archetypeId; BuildAudienceScreen restores from the full selections
 *  + studyId. Both are populated so either destination finds what it reads. */
export interface AudienceStepState {
  useCase?: string | null;
  selections?: StudySelection[];
  twinCount?: number;
  paid?: boolean;
  studyId?: number;
  subCategory?: string;
  archetypeId?: string;
}

/** Build the back-to-audience navigation state once, so FillStudyScreen and
 *  ReviewScreen don't hand-roll the same {…, subCategory, archetypeId} block. */
export const audienceStepState = (args: {
  useCase?: string | null;
  selections?: StudySelection[];
  twinCount?: number;
  paid?: boolean;
  studyId?: number;
}): AudienceStepState => {
  const [first] = args.selections ?? [];
  return {
    useCase: args.useCase,
    selections: args.selections,
    twinCount: args.twinCount,
    paid: args.paid,
    studyId: args.studyId,
    subCategory: first?.sub_category,
    archetypeId: first?.archetype_id,
  };
};

// Public marketing site — the header logo links here intentionally (opens a new
// tab, so it never discards an in-progress study).
export const MARKETING_SITE_URL = 'https://blue-pill.ai/';

// Where a marketplace logout lands. A full external redirect to the public
// marketing marketplace page (not the in-app /marketplace route), so a logged-out
// user leaves the app entirely rather than sitting on an authed-looking shell.
export const MARKETPLACE_LOGOUT_URL = 'https://blue-pill.ai/marketplace';

// The headline "real human participants" number shown across the marketing
// surfaces (landing hero, signup panel). A deliberate round marketing figure —
// kept a constant on purpose (not the live distinct count) so it reads 1,000.
export const PARTICIPANT_COUNT = 1000;
export const PARTICIPANT_LABEL = PARTICIPANT_COUNT.toLocaleString();
